import React from 'react';
import {
  Box,
  Grid,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Reply from '@mui/icons-material/Reply';
import { i18n } from '@lingui/core';
import {
  buildConversationMessageClassName,
  processMessageMentions,
} from '../mentions/helper';
import {
  ContactSchema,
  ConversationThreadAttachmentSchema,
  ConversationThreadMentions,
  UserSchema,
} from '../../definitions/schemas';
import { DSR_MENTION_SELECTOR } from '../Input/helper';
import {
  formatDateTime,
  getDateFromEpoch,
  getFormattedDistance,
} from '../../lib/Date';
import { getDisplayName } from '../../lib/DisplayName';
import { OrganizationFlagEnum } from '../../lib/features/FeatureFlag';
import { QUERY_STRING } from '../../enums/QueryString';
import { useI18n } from '../../lib/hook/useI18n';
import AnonymousIdentityGuard from '../Room/View/AnonymousIdentityGuard';
import GuestAvatar, { GuestAvatarProps } from '../Guest/GuestAvatar';
import HtmlDisplay from '../HtmlDisplay';
import MessageAttachment from './MessageAttachment';
import MessageCard from './MessageCard';
import ThemableButton from '../../theme/components/Button/ThemableButton';
import useFeatureFlag from '../../lib/hook/useFeatureFlag';

// point to '0', to auto-open the conversation drawer
export const DEFAULT_ROOM_MESSAGE_ID = '0';

const useStyles = makeStyles((theme: Theme) => ({
  listItem: {
    marginTop: 0,
    [`& ${DSR_MENTION_SELECTOR}`]: {
      background: 'var(--info-color-background)',
      borderRadius: theme.spacing(0.5),
      color: theme.palette.secondary.main,
      padding: theme.spacing(0.25),
    },
  },
  message: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  replyButton: {
    cursor: 'pointer',
    fontSize: theme.typography.body2.fontSize,
    padding: '0 5px',
    textTransform: 'none',
  },
  deleteButton: {
    cursor: 'pointer',
    padding: '0',
  },
  avatar: {
    height: theme.spacing(5),
    width: theme.spacing(5),
  },
  listItemAvatar: {
    marginTop: theme.spacing(0.25),
    minWidth: theme.spacing(6.5),
  },
}));

interface Props {
  attachmentReferrer: string;
  attachments?: ConversationThreadAttachmentSchema[];
  avatarProps?: Partial<GuestAvatarProps>;
  createdAt: number;
  deletable?: boolean;
  disableGutters?: boolean;
  isThread?: boolean;
  mentions?: ConversationThreadMentions[];
  message?: string; // ConversationThreadSchema['message']
  messageId?: string;
  onDelete?: () => void;
  onReply?: () => void;
  owner: UserSchema | ContactSchema;
  replyable?: boolean;
  replyReferrer?: string;
}

const ConversationMessage = React.forwardRef<HTMLLIElement, Props>(
  (props: Props, ref): JSX.Element => {
    const { t } = useI18n();
    const classes = useStyles();

    const areSmsNotificationsEnabled = useFeatureFlag(
      OrganizationFlagEnum.SMS_NOTIFICATIONS
    );

    const {
      attachmentReferrer,
      attachments,
      avatarProps,
      createdAt,
      deletable,
      disableGutters = false,
      isThread,
      mentions,
      message,
      messageId = '0',
      onDelete,
      onReply,
      owner,
      replyable = false,
      replyReferrer = '',
    } = props;

    const getAuthor = (): string =>
      getDisplayName(owner as ContactSchema, { maskPhoneNumber: true });

    const renderDeleteOption = (): JSX.Element | undefined =>
      !deletable ? undefined : (
        <Tooltip
          arrow
          placement="top"
          PopperProps={{ disablePortal: true }}
          title={isThread ? 'Delete thread' : 'Delete post'}
          enterTouchDelay={0}
          leaveTouchDelay={5000}
        >
          <Box display="inline-block" mr={-0.75}>
            <IconButton
              aria-label="delete"
              onClick={() => {
                if (onDelete) {
                  onDelete();
                }
              }}
            >
              <DeleteOutline color="disabled" fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
      );

    const renderEllapsedTime = (): JSX.Element => {
      const creationDate = getDateFromEpoch(createdAt);
      const formattedDate = formatDateTime(createdAt);
      const elapsedTime = getFormattedDistance(creationDate, i18n.locale);

      return (
        <Box pt={0.25}>
          <Typography
            color="textSecondary"
            title={formattedDate}
            variant="body2"
          >
            {elapsedTime}
          </Typography>
        </Box>
      );
    };

    const renderReply = (): JSX.Element | undefined => {
      if (!replyable) {
        return renderDeleteOption();
      }

      return (
        <AnonymousIdentityGuard
          queryParams={{ [QUERY_STRING.ROOM_MESSAGE_ID]: messageId }}
          referrer={replyReferrer}
        >
          <ThemableButton
            className={classes.replyButton}
            onClick={onReply}
            size="small"
            startIcon={<Reply fontSize="small" />}
            variant="text"
          >
            {t('conversation.drawer.reply')}
          </ThemableButton>
          {renderDeleteOption()}
        </AnonymousIdentityGuard>
      );
    };

    const processMessage = (message: string): string => {
      if (!areSmsNotificationsEnabled) {
        return message;
      }

      return processMessageMentions(message);
    };

    const renderMessage = () => {
      let content;

      if (message) {
        content = (
          <HtmlDisplay linkify text={processMessage(message)} variant="body2" />
        );
      }

      if (attachments && attachments.length > 0) {
        content = (
          <>
            {content}
            <Box mt={1}>
              <Grid container spacing={2}>
                {attachments.map(({ id, filename, mime, uri }) => (
                  <Grid key={id} item xs={12} sm={6}>
                    <AnonymousIdentityGuard
                      queryParams={{
                        [QUERY_STRING.ROOM_MESSAGE_ID]: messageId,
                      }}
                      referrer={attachmentReferrer}
                    >
                      <MessageAttachment
                        filename={filename!}
                        mime={mime!}
                        url={uri}
                      />
                    </AnonymousIdentityGuard>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </>
        );
      }

      return (
        <>
          {content}
          <MessageCard mentions={mentions} messageId={messageId} />
        </>
      );
    };

    return (
      <ListItem
        alignItems="flex-start"
        className={buildConversationMessageClassName(messageId)}
        disableGutters={disableGutters}
        ref={ref}
      >
        <ListItemAvatar className={classes.listItemAvatar}>
          <GuestAvatar
            className={classes.avatar}
            guest={owner}
            {...avatarProps}
          />
        </ListItemAvatar>
        <ListItemText
          classes={{ multiline: classes.listItem }}
          disableTypography
          primary={
            <Box
              alignItems="center"
              display="flex"
              flexWrap="wrap"
              justifyContent="space-between"
            >
              <Typography color="textPrimary" component="div" variant="body2">
                <Box component="strong">{getAuthor()}</Box>
                {renderEllapsedTime()}
              </Typography>
              <Box textAlign="right">{renderReply()}</Box>
            </Box>
          }
          secondary={renderMessage()}
        />
      </ListItem>
    );
  }
);

export default ConversationMessage;
